import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen, ArrowRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const AREvsARCESimulator = () => {
  const [inputs, setInputs] = useState({
    areMensuelle: '1500',
    droitsRestants: '18',
    revenuPrevisionnel: '2000'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const areMensuelle = parseFloat(inputs.areMensuelle) || 0;
    const dureeMois = parseFloat(inputs.droitsRestants) || 18;
    const revenuPrev = parseFloat(inputs.revenuPrevisionnel) || 0;

    const droitsTotaux = areMensuelle * dureeMois;

    // Option 1: Maintien ARE mensuel
    const areCumulee = revenuPrev > 0 ? Math.max(0, areMensuelle - (revenuPrev * 0.70)) : areMensuelle;
    const totalMaintien = areCumulee * dureeMois;
    const revenuMensuelARE = areCumulee + revenuPrev;

    // Option 2: ARCE (60% des droits restants en capital)
    const capitalARCE = droitsTotaux * 0.60;
    const premierVersement = capitalARCE * 0.50;
    const secondVersement = capitalARCE * 0.50;
    const droitsRestantsApresARCE = droitsTotaux * 0.40;

    // Comparaison
    const avantageARCE = capitalARCE - totalMaintien;

    setResults({
      droitsTotaux: Math.round(droitsTotaux),
      areCumulee: Math.round(areCumulee),
      totalMaintien: Math.round(totalMaintien),
      revenuMensuelARE: Math.round(revenuMensuelARE),
      capitalARCE: Math.round(capitalARCE),
      premierVersement: Math.round(premierVersement),
      secondVersement: Math.round(secondVersement),
      droitsRestantsApresARCE: Math.round(droitsRestantsApresARCE),
      avantageARCE: Math.round(avantageARCE),
      meilleur: avantageARCE > 1000 ? 'ARCE' : avantageARCE < -1000 ? 'ARE' : 'Similaire'
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'ARE vs ARCE - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Maintien ARE vs ARCE', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`ARE mensuelle : ${parseInt(inputs.areMensuelle).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Droits restants : ${inputs.droitsRestants} mois`, 15, y); y += 7;
    doc.text(`Droits totaux : ${results.droitsTotaux?.toLocaleString()} EUR`, 15, y); y += 12;
    doc.text(`--- Maintien ARE ---`, 15, y); y += 7;
    doc.text(`Total perçu : ${results.totalMaintien?.toLocaleString()} EUR`, 15, y); y += 10;
    doc.text(`--- ARCE (capital) ---`, 15, y); y += 7;
    doc.text(`Capital ARCE : ${results.capitalARCE?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`1er versement : ${results.premierVersement?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`2nd versement (6 mois) : ${results.secondVersement?.toLocaleString()} EUR`, 15, y); y += 12;
    doc.setFontSize(13); doc.setTextColor(30, 64, 175);
    doc.text(`Recommandation : ${results.meilleur}`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : France Travail 2026, Unédic, ARCE 60% depuis juillet 2023', 15, y);
    doc.save('simulation-are-vs-arce-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Paramètres
            </CardTitle>
            <CardDescription>Comparez le maintien ARE et l'ARCE en capital</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>ARE mensuelle actuelle (€)</Label>
              <Input data-testid="are-input" type="text" inputMode="numeric" value={inputs.areMensuelle}
                onChange={(e) => handleInputChange('areMensuelle', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="1500" className="mt-1" />
            </div>
            <div>
              <Label>Mois de droits restants</Label>
              <Input data-testid="droits-input" type="text" inputMode="numeric" value={inputs.droitsRestants}
                onChange={(e) => handleInputChange('droitsRestants', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="18" className="mt-1" />
            </div>
            <div>
              <Label>Revenus mensuels prévus de l'activité (€)</Label>
              <Input data-testid="revenu-input" type="text" inputMode="numeric" value={inputs.revenuPrevisionnel}
                onChange={(e) => handleInputChange('revenuPrevisionnel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="2000" className="mt-1" />
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-800">Droits totaux estimés : <span className="font-bold">{results.droitsTotaux?.toLocaleString()}€</span></p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>ARCE = 60% des droits ARE restants (depuis 01/07/2023)</li>
                    <li>Versement en 2 fois : 50% immédiat, 50% à 6 mois</li>
                    <li>Condition : bénéficier de l'ACRE</li>
                    <li>40% restants récupérables si arrêt de l'activité</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">Maintien ARE</CardTitle>
            <Badge className={`mx-auto ${results.meilleur === 'ARE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {results.meilleur === 'ARE' ? 'Recommandé' : 'Mensuel'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">ARE cumulée / mois</p>
              <p className="text-xl font-bold text-blue-900">{results.areCumulee?.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600 mb-1">Revenu total / mois</p>
              <p className="text-xl font-bold text-green-900">{results.revenuMensuelARE?.toLocaleString()}€</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-xs text-green-600 mb-1">Total perçu sur la période</p>
              <p className="text-2xl font-black text-green-900" data-testid="result-total-are">{results.totalMaintien?.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center text-xs text-amber-800">
              Versement mensuel • Filet de sécurité si l'activité ne décolle pas
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">ARCE (Capital)</CardTitle>
            <Badge className={`mx-auto ${results.meilleur === 'ARCE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {results.meilleur === 'ARCE' ? 'Recommandé' : 'Capital'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">1er versement (immédiat)</p>
              <p className="text-xl font-bold text-blue-900">{results.premierVersement?.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">2nd versement (6 mois)</p>
              <p className="text-xl font-bold text-blue-900">{results.secondVersement?.toLocaleString()}€</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-xs text-green-600 mb-1">Capital total ARCE</p>
              <p className="text-2xl font-black text-green-900" data-testid="result-capital-arce">{results.capitalARCE?.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center text-xs text-purple-800">
              Capital pour investir • Reste {results.droitsRestantsApresARCE?.toLocaleString()}€ en réserve
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`p-4 rounded-lg flex-1 text-center ${results.meilleur === 'ARCE' ? 'bg-green-50 border-2 border-green-300' : results.meilleur === 'ARE' ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-50'}`}>
              <p className="text-lg font-bold">
                {results.meilleur === 'ARCE' ? `L'ARCE vous apporte ${results.avantageARCE?.toLocaleString()}€ de plus` :
                 results.meilleur === 'ARE' ? `Le maintien ARE vous apporte ${Math.abs(results.avantageARCE)?.toLocaleString()}€ de plus` :
                 'Les deux options sont proches'}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 w-full md:w-auto">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">Sources : France Travail 2026 | ARCE 60% (Unédic) | service-public.fr | francetravail.org</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AREvsARCESimulator;
